import { MouseEvent, useMemo } from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import {
  IBriefUserInfoFieldsFragment,
  ISearchUsersQuery,
  ISearchUsersQueryVariables,
  UserState,
} from '../../../../utils/apollo/graphqlSchema';
import useUser from '../../../../utils/hooks/useUser';
import { SEARCH_USERS_QUERY } from '../../../../utils/apollo/users';
import { StyledSingleSelectOption } from '../../../Dropdown/SingleSelect';
import useNavigateSuggestions from './utils/useNavigateSuggestions';

interface IProps {
  term: string;
  onSelect: (user: IBriefUserInfoFieldsFragment) => void;
  participants?: IBriefUserInfoFieldsFragment[];
}

const NoResultsWrapper = styled.div`
  padding: 8px;
  text-align: center;
`;

const userStates = [UserState.Active];

const MentionSuggestions = ({ term, onSelect, participants }: IProps) => {
  const currentUser = useUser();

  const { data, loading } = useQuery<
    ISearchUsersQuery,
    ISearchUsersQueryVariables
  >(SEARCH_USERS_QUERY, {
    variables: {
      pagination: {
        first: 10,
        after: 0,
      },
      term,
      includes: [],
      userStates,
    },
  });

  const entities = data?.users.entities;
  const sortedUsers = useMemo(() => {
    if (!entities?.length) {
      return [];
    }

    if (!participants?.length) {
      return entities;
    }

    // Most likely user wants to mention someone from conversation,
    // so add thread participants to the top of the list
    return [
      ...participants,
      ...entities.filter(
        (user) =>
          !participants.some((participant) => participant.id === user.id),
      ),
    ];
  }, [participants, entities]);
  const listSize = sortedUsers.length;

  const handleSelectFromKeyboard = (index: number) => {
    if (sortedUsers[index]) {
      onSelect(sortedUsers[index]);
    }
  };

  const activeIndex = useNavigateSuggestions(
    listSize,
    handleSelectFromKeyboard,
  );

  const handleSelect =
    (user: IBriefUserInfoFieldsFragment) => (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onSelect(user);
    };

  return (
    <div>
      {loading ? (
        <NoResultsWrapper>Loading...</NoResultsWrapper>
      ) : sortedUsers.length ? (
        sortedUsers.map((user, userIndex) => (
          <StyledSingleSelectOption
            key={user.id}
            onClick={handleSelect(user)}
            title={user.email}
            isFocused={activeIndex === userIndex}
          >
            {user?.name ?? user.email}
            {user.id === currentUser.id && ' (You)'}
          </StyledSingleSelectOption>
        ))
      ) : (
        <NoResultsWrapper>No results</NoResultsWrapper>
      )}
    </div>
  );
};

export default MentionSuggestions;
